import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from 'Hooks/useIsMobile';
import { useScrollDirection } from 'Hooks/useScrollDirection';

import './BottomNavBar.css';

// Helper to check if current time is within a UTC window (same logic as TppNavBar)
const isWithinUtcWindow = (startUtc, endUtc) => {
  if (!startUtc || !endUtc) return false;
  const start = new Date(startUtc.endsWith('Z') ? startUtc : startUtc + 'Z');
  const end = new Date(endUtc.endsWith('Z') ? endUtc : endUtc + 'Z');
  const now = new Date();
  return now.getTime() >= start.getTime() && now.getTime() <= end.getTime();
};

const BottomNavBar = () => {
  const { t } = useTranslation('navigation');
  const { isMobile } = useIsMobile();
  const scrollDirection = useScrollDirection();
  const location = useLocation();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const currentUser = useSelector((state) => state.currentUser);
  const settings = useSelector((state) => state.settings);

  if (!isMobile) return null;

  const isAssignmentsVisible = () => {
    const { acceptDeclineStartDate, acceptDeclineEndDate } = settings || {};
    if (!acceptDeclineStartDate || !acceptDeclineEndDate) return false;
    return isWithinUtcWindow(acceptDeclineStartDate, acceptDeclineEndDate);
  };

  const isInterestFormVisible = () => {
    const { applyStartDate, applyEndDate } = settings || {};
    if (!applyStartDate || !applyEndDate) return false;
    return isWithinUtcWindow(applyStartDate, applyEndDate);
  };

  const allTabs = [
    { path: '/mtp/profile', icon: 'bi bi-person', labelKey: 'profile', visible: true },
    {
      path: '/mtp/rep-work-application',
      icon: 'bi bi-file-earmark-text',
      labelKey: 'interestForm',
      visible: isInterestFormVisible(),
    },
    { path: '/mtp/assignments', icon: 'bi bi-briefcase', labelKey: 'assignments', visible: isAssignmentsVisible() },
    {
      path: '/mtp/manage-rep',
      icon: 'bi bi-people',
      labelKey: 'manageRep',
      visible: currentUser?.isManager && settings?.commentsOpen === 'Yes',
    },
    { path: '/mtp/settings', icon: 'bi bi-gear', labelKey: 'settings', visible: true },
  ];

  const visibleTabs = allTabs.filter((tab) => tab.visible);

  const isActive = (tabPath) => location.pathname === tabPath || location.pathname.startsWith(tabPath + '/');

  const isHidden = scrollDirection === 'down';

  const hiddenClass = isHidden ? 'bottom-nav--hidden' : '';
  const reducedMotionClass = prefersReducedMotion ? 'bottom-nav--no-motion' : '';

  return (
    <nav
      className={`bottom-nav ${hiddenClass} ${reducedMotionClass}`.trim()}
      role="navigation"
      aria-label={t('mobileNavigation')}
    >
      {visibleTabs.map((tab) => {
        const active = isActive(tab.path);
        return (
          <button
            key={tab.path}
            className={`bottom-nav__tab ${active ? 'bottom-nav__tab--active' : ''}`}
            onClick={() => navigate(tab.path)}
            aria-current={active ? 'page' : undefined}
            type="button"
          >
            <i className={tab.icon} aria-hidden="true" />
            <span className="bottom-nav__label">{t(tab.labelKey)}</span>
            {active && (
              <motion.div
                className="bottom-nav__indicator"
                layoutId="activeTab"
                transition={prefersReducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNavBar;
